// src/embed/loader.ts
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { WidgetApp } from "./WidgetApp";
// CSS goes into the widget's shadow root, not the host page's <head>
import widgetCss from "./widget.css?inline";
import { normalizePreChat, normalizeProactive } from "./engagement";

export type WidgetMountOptions = {
    apiBase: string;
    realtimeUrl: string;

    tenantHandle: string;
    widgetKey: string;

    title?: string;
    position?: "left" | "right";

    // default launcher distance (px) from the side edge / from the bottom, e.g. to clear
    // the host page's own bottom-corner UI. A visitor's dragged position still wins.
    // Use this instead of overriding `.cw { bottom }` in CSS — an !important there
    // breaks vertical dragging.
    offsetX?: number;
    offsetY?: number;

    // "auto" (default: visitor's browser language), "en" or "sr". Overrides the widget
    // setting from the admin console.
    language?: "auto" | "en" | "sr";

    // opcionalno: sticky visitor na sajtu (ako ne proslediš, čuva se u localStorage)
    externalVisitorId?: string;

    // NEW: preview overrides (ili ručno zadavanje preko snippet-a)
    primaryColor?: string;
    launcherEmoji?: string;

    // pre-chat form / proactive greeting; normally from the widget's settings in the
    // console - these override them (the console's live preview uses them)
    preChatForm?: unknown;
    proactive?: unknown;
    // console preview: show the greeting every time (no once-per-visit memory)
    preview?: boolean;


};

type BootstrapRes = {
    token: string;
    conversationId: string;
    settings: Record<string, any>;
    anyAgentOnline?: boolean;
    contactKnown?: { name: boolean; email: boolean };
    conversationStarted?: boolean;
    agentStatus?: "online" | "away" | "offline";
};

const roots = new WeakMap<HTMLElement, Root>();

// The widget lives in a shadow root on its own host element at the end of <body>, so the
// host page's CSS can't restyle it (and ours can't leak out). `el` from the snippet is
// only the key for re-mounts; nothing is rendered inside it.
function createShadowContainer(): HTMLElement {
    const host = document.createElement("div");
    host.setAttribute("data-helpchat", "");
    document.body.appendChild(host);

    const shadow = host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = widgetCss;
    shadow.appendChild(style);

    const container = document.createElement("div");
    shadow.appendChild(container);
    return container;
}

function normalizeBase(url: string) {
    return (url || "").replace(/\/+$/, "");
}

function getOrCreateExternalVisitorId(provided?: string) {
    if (provided && provided.trim()) return provided.trim();

    const key = "cw_external_visitor_id";
    try {
        const existing = localStorage.getItem(key);
        if (existing && existing.trim()) return existing.trim();

        const fresh =
            (globalThis.crypto && "randomUUID" in globalThis.crypto)
                ? globalThis.crypto.randomUUID()
                : `v-${Math.random().toString(16).slice(2)}${Date.now()}`;

        localStorage.setItem(key, fresh);
        return fresh;
    } catch {
        return `v-${Math.random().toString(16).slice(2)}${Date.now()}`;
    }
}

async function bootstrap(apiBase: string, tenantHandle: string, widgetKey: string, externalVisitorId?: string) {
    const base = normalizeBase(apiBase);

    // VAŽNO: kod tebe je prefix /public
    const res = await fetch(`${base}/public/widget/bootstrap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            tenantHandle,
            widgetKey,
            externalVisitorId: externalVisitorId ?? null,
        }),
    });

    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`bootstrap failed (${res.status}) ${txt}`);
    }
    return (await res.json()) as BootstrapRes;
}

export async function mountWidget(el: HTMLElement, opts: WidgetMountOptions) {
    const apiBase = normalizeBase(opts.apiBase);
    const realtimeUrl = normalizeBase(opts.realtimeUrl);

    let root = roots.get(el);
    if (!root) {
        root = createRoot(createShadowContainer());
        roots.set(el, root);
    }

    const externalVisitorId = getOrCreateExternalVisitorId(opts.externalVisitorId);

    // ✅ doBootstrap wrapper (kao što si napisao)
    const doBootstrap = () => bootstrap(apiBase, opts.tenantHandle, opts.widgetKey, externalVisitorId);

    const b = await doBootstrap();

    const mergedTitle = (b.settings?.widgetTitle as string) || opts.title;
    const mergedPos = (b.settings?.position as "left" | "right") || opts.position;

    // NEW: allow live override (preview) OR fallback to server settings
    const mergedPrimaryColor = String(opts.primaryColor || b.settings?.primaryColor || "#111827");
    const mergedEmoji = String(opts.launcherEmoji || b.settings?.launcherEmoji || "💬");
    const mergedLanguage = String(opts.language || b.settings?.language || "auto");
    const preChatForm = normalizePreChat(opts.preChatForm ?? b.settings?.preChatForm);
    const proactive = normalizeProactive(opts.proactive ?? b.settings?.proactive);

    root.render(
        <React.StrictMode>
            <WidgetApp
                apiBase={apiBase}
                realtimeUrl={realtimeUrl}
                token={b.token}
                conversationId={b.conversationId}
                anyAgentOnline={b.anyAgentOnline}
                agentStatus={b.agentStatus}
                title={mergedTitle}
                position={mergedPos}
                offsetX={opts.offsetX}
                offsetY={opts.offsetY}
                language={mergedLanguage}
                primaryColor={mergedPrimaryColor}
                launcherEmoji={mergedEmoji}
                preChatForm={preChatForm}
                proactive={proactive}
                contactKnown={b.contactKnown}
                conversationStarted={!!b.conversationStarted}
                preview={!!opts.preview}
                onRebootstrap={doBootstrap}
            />
        </React.StrictMode>
    );
}

export async function mount(selector: string, opts: WidgetMountOptions) {
    const el = document.querySelector(selector);
    if (!el) throw new Error(`mount: element not found: ${selector}`);
    return mountWidget(el as HTMLElement, opts);
}

declare global {
    interface Window {
        ChatWidget?: any;
    }
}
window.ChatWidget = { mount, mountWidget };
